import styled from 'styled-components';
import { Box } from '@components/Box';
import { Button } from '@components/Button';
import { CTALink } from '@components/CTALink';
import { TextPairing } from '@components/TextPairing';
import { CloudImage } from '@components/CloudImage';
import { Flex } from '@components/Flex';
import { Columns } from '@components/Columns';
import { themeMediaQuery, themeSpace, themeColor } from '@utils/styledTheme.util';

export const DesktopWrapper = styled.div`
  display: block;
  ${themeMediaQuery('below_sm')} {
    display: none;
  }
`;

export const MobileWrapper = styled.div`
  display: none;
  ${themeMediaQuery('below_sm')} {
    display: block;
  }
`;

export const WeddingWebsiteWrapper = styled(Box)<{ page: string }>`
  position: relative;
  ${themeMediaQuery('xl')} {
    overflow: hidden;
  }
  ${themeMediaQuery('below_sm')} {
    // Setting different height for different page because content length is not variable and auto not workable because position
    height: ${({ page }) => (page === 'save-the-date' ? '1050px' : 'auto')};
  }
`;

export const SectionTextPairingWrapper = styled(Box)`
  position: relative;
  z-index: 1;
`;

export const StyledCloudImage = styled(CloudImage)`
  width: 100%;
  border-radius: 16px;
  ${themeMediaQuery('below_sm')} {
    border-radius: 4px;
  }
`;

export const StyledInfoImage = styled(CloudImage)`
  width: 91%;
`;

export const TextPairingWrapper = styled(TextPairing)<{ page: string }>`
  .text-pairing-title {
    max-width: ${({ page }) => (page === 'rsvp' ? '390px' : page === 'registry' ? '320px' : page === 'guest-list' ? '340px' : '460px')};
  }
  .text-pairing-body {
    max-width: ${({ page }) => (page === 'guest-list' ? '340px' : '280px')};
  }
  ${themeMediaQuery('below_sm')} {
    display: none;
  }
  ${themeMediaQuery('xl')} {
    .text-pairing-title {
      max-width: ${({ page }) => (page === 'save-the-date' ? '600px' : page === 'guest-list' ? '455px' : '540px')};
    }
    .text-pairing-body {
      max-width: ${({ page }) => (page === 'guest-list' ? '400px' : '340px')};
    }
  }
`;

export const StyledMobileTextPairingWrapper = styled(TextPairing)<{ page: string }>`
  ${themeMediaQuery('sm')} {
    display: none;
  }
  .text-pairing-title {
    max-width: ${({ page }) => (page === 'guest-list' ? '210px' : '270px')};
  }
  .text-pairing-body {
    max-width: 270px;
  }
`;

export const ExploreMoreWrapper = styled(Button)`
  background: transparent;
  border: 1px solid #fff;
  color: #fff;
`;

export const ExploreMoreBlueVariantWrapper = styled(Button)`
  background: transparent;
  color: ${themeColor('purple5')};
  border: 1px solid ${themeColor('purple5')};
  &:hover {
    color: #fff;
  }
`;

export const LearnMoreWrapper = styled(CTALink)`
  color: #fff;
  &:hover {
    color: #fff;
  }
  ${themeMediaQuery('below_sm')} {
    margin: ${themeSpace(0)} auto;
  }
`;

export const FlexWrapper = styled(Flex)`
  max-width: 500px;
  margin-top: ${themeSpace(7)};
  ${themeMediaQuery('below_sm')} {
    flex-direction: column;
  }
`;
export const FlexWrapperContainer = styled(Flex)`
  max-width: 500px;
  margin: ${themeSpace(0)} auto;
  margin-top: ${themeSpace(6)};
  ${themeMediaQuery('below_sm')} {
    flex-direction: column;
  }
`;

export const ColumnImageWrapper = styled(Columns)`
  margin-top: ${themeSpace(11)};
  margin-bottom: ${themeSpace(11)};
  position: relative;
  ${themeMediaQuery('below_sm')} {
    display: flex;
    top: -370px;
  }
`;

export const StyledSTDTextWrapper = styled(Columns)`
  ${themeMediaQuery('below_sm')} {
    position: relative;
    top: 120px;
  }
`;

export const ColumnInfoImageWrapper = styled(Columns.Column)`
  margin-top: ${themeSpace(11)};
`;

export const StyledImageWrapper = styled(Box)<{ pageVariant: string }>`
  position: absolute;
  left: ${({ pageVariant }) => (pageVariant === 'registry' ? '6%' : '-14%')};
  top: ${({ pageVariant }) => (pageVariant === 'registry' ? '265px' : '95px')};
  width: ${({ pageVariant }) => (pageVariant === 'registry' ? '45%' : '58%')};
  ${themeMediaQuery('below_sm')} {
    left: ${({ pageVariant }) => (pageVariant === 'registry' ? '5%' : '-6%')};
    top: ${({ pageVariant }) => (pageVariant === 'registry' ? '130px' : '20px')};
    width: ${({ pageVariant }) => (pageVariant === 'registry' ? '90%' : '100%')};
  }
`;

export const StyledSTDImageWrapper = styled(Box)`
  ${themeMediaQuery('below_sm')} {
    width: 80%;
    position: absolute;
    top: 160px;
    right: -32px;
    z-index: 0;
  }
`;

export const StyledCardWrapper = styled(Box)`
  ${themeMediaQuery('below_sm')} {
    width: 80%;
    position: relative;
    z-index: 1;
  }
`;

export const StyledImageArtsWrapper = styled(CloudImage)<{
  page: string | undefined;
  position: string | undefined;
  top: string | number | undefined;
  bottom?: string | number | undefined;
  left?: string | number | undefined;
  right?: string | number | undefined;
  $imgWidth?: string | number | undefined;
  zindex?: string | number | undefined;
  transform?: string | number | undefined;
  $artPlacement: string;
}>`
  position: ${({ position }) => position};
  top: ${({ top }) => top};
  bottom: ${({ bottom }) => bottom};
  left: ${({ left }) => left};
  right: ${({ right }) => right};
  width: ${({ $imgWidth }) => $imgWidth};
  zindex: ${({ zindex }) => zindex};
  transform: ${props => props.transform};
  ${themeMediaQuery('below_sm')} {
    // Conditionally set width for art assets depends on the size of the page variant.
    width: ${({ page, $artPlacement }) => (page === 'save-the-date' ? ($artPlacement === 'bottomLeft' ? '80%' : '0%') : '100%')};

    // Set top property for the arts that have artPlacement top otherwise unset for bottom property
    top: ${({ $artPlacement }) => ($artPlacement.includes('top') ? '0px' : 'unset')};
    left: 0px;
    right: 0px;

    // Set bottom property for the arts that have artPlacement bottom otherwise unset for top property
    bottom: ${({ $artPlacement }) => ($artPlacement.includes('bottom') ? '0px' : 'unset')};
    transform: unset;
  }
`;
